import React, { useState, useEffect } from 'react'
import Modal from './Modal'
import StarRating from './StarRating'
import { RecipeRating } from '../services/FirestoreService'
import './RatingModal.css'

interface RatingModalProps {
  isOpen: boolean
  onClose: () => void
  recipeId: string
  recipeTitle: string
  existingRating?: RecipeRating | null
  onSubmit: (rating: Omit<RecipeRating, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<void>
}

const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  onClose,
  recipeId,
  recipeTitle,
  existingRating,
  onSubmit
}) => {
  const [rating, setRating] = useState<number>(existingRating?.rating || 0)
  const [notes, setNotes] = useState<string>(existingRating?.notes || '')
  const [wouldMakeAgain, setWouldMakeAgain] = useState<boolean>(existingRating?.would_make_again ?? true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form when modal opens with different recipe
  useEffect(() => {
    if (isOpen) {
      setRating(existingRating?.rating || 0)
      setNotes(existingRating?.notes || '')
      setWouldMakeAgain(existingRating?.would_make_again ?? true)
      setError(null)
    }
  }, [isOpen, existingRating])

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a rating')
      return
    }

    setSaving(true)
    setError(null)

    try {
      await onSubmit({
        recipe_id: recipeId,
        recipe_title: recipeTitle,
        rating: rating as 1 | 2 | 3 | 4 | 5,
        notes: notes.trim() || null,
        would_make_again: wouldMakeAgain
      })
      onClose()
    } catch (err) {
      setError('Failed to save rating. Please try again.')
      console.error('Error saving rating:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rate Recipe">
      <div className="rating-modal-content">
        <h3 className="recipe-title">{recipeTitle}</h3>

        <div className="rating-section">
          <label>How would you rate this recipe?</label>
          <StarRating
            rating={rating}
            onRatingChange={setRating}
            size="large"
          />
        </div>

        <div className="make-again-section">
          <label>Would you make this again?</label>
          <div className="toggle-buttons">
            <button
              type="button"
              className={`toggle-btn ${wouldMakeAgain ? 'active' : ''}`}
              onClick={() => setWouldMakeAgain(true)}
            >
              Yes
            </button>
            <button
              type="button"
              className={`toggle-btn ${!wouldMakeAgain ? 'active' : ''}`}
              onClick={() => setWouldMakeAgain(false)}
            >
              No
            </button>
          </div>
        </div>

        <div className="notes-section">
          <label htmlFor="rating-notes">Notes (optional)</label>
          <textarea
            id="rating-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any thoughts about the recipe? Modifications you made?"
            rows={3}
            maxLength={500}
          />
          <span className="char-count">{notes.length}/500</span>
        </div>

        {error && <p className="error-message">{error}</p>}

        <div className="modal-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="submit-btn"
            onClick={handleSubmit}
            disabled={saving || rating === 0}
          >
            {saving ? 'Saving...' : existingRating ? 'Update Rating' : 'Submit Rating'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default RatingModal
